import React from "react";
import { floorFromFlat, floorLabel } from "../Helper/addressBlocks";

/**
 * Block + flat entry for apartment addresses.
 *
 * The block is chosen from buttons, never typed. That is the entire fix for the
 * inconsistency this replaces: when the block was a text input, one physical
 * tower arrived as "G block", "G Block", "block G", "g-block", "G-304" and "G",
 * so the rider app could not group orders by tower. Everyone now picks the same
 * string off the same admin-defined list, so the values are byte-identical.
 *
 * Floor is shown but not editable — it is derived from the flat number, because
 * when customers typed it themselves the column filled up with "7th floor",
 * "2 nd", "Ground", "A" and "304", sometimes contradicting their own flat.
 *
 * Used by the address form and by the one-time confirmation popup, so both
 * always offer exactly the same choices.
 */
const BlockPicker = ({
  blocks = [],
  block,
  flat,
  onBlockChange,
  onFlatChange,
  disabled = false,
  showLabels = true,
  inputStyle,
}) => {
  const derivedFloor = floorFromFlat(flat);

  const baseInputStyle = inputStyle || {
    width: "100%",
    height: "44px",
    borderRadius: "12px",
    padding: "8px 16px",
    border: "0.4px solid #6B8E23",
    background: "#FAFAFA",
    fontSize: "14px",
  };

  // No blocks configured for this hub yet. Rather than showing an empty row of
  // buttons that cannot be pressed, fall back to a plain input so the customer
  // can still save an address while ops fills in the list.
  const hasBlocks = Array.isArray(blocks) && blocks.length > 0;

  return (
    <div style={{ width: "100%" }}>
      {showLabels && (
        <label
          style={{
            fontSize: "13px",
            fontWeight: 600,
            color: "#333",
            marginBottom: "8px",
            display: "block",
          }}
        >
          Block / Tower <span style={{ color: "#C0392B" }}>*</span>
        </label>
      )}

      {hasBlocks ? (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            marginBottom: "16px",
          }}
        >
          {blocks.map((option) => {
            const selected = option === block;
            return (
              <button
                key={option}
                type="button"
                disabled={disabled}
                onClick={() => onBlockChange(option)}
                aria-pressed={selected}
                style={{
                  minWidth: "52px",
                  height: "44px",
                  padding: "0 14px",
                  borderRadius: "12px",
                  border: selected
                    ? "1.5px solid #6B8E23"
                    : "0.4px solid #C7C7C7",
                  background: selected ? "#6B8E23" : "#FAFAFA",
                  color: selected ? "#FFFFFF" : "#333333",
                  fontSize: "14px",
                  fontWeight: selected ? 600 : 400,
                  cursor: disabled ? "not-allowed" : "pointer",
                  opacity: disabled ? 0.6 : 1,
                  transition: "all 0.15s ease",
                }}
              >
                {option}
              </button>
            );
          })}
        </div>
      ) : (
        <input
          type="text"
          value={block || ""}
          disabled={disabled}
          onChange={(e) => onBlockChange(e.target.value)}
          placeholder="Block / Tower *"
          style={{ ...baseInputStyle, marginBottom: "16px" }}
        />
      )}

      <div style={{ display: "flex", gap: "12px", alignItems: "flex-end" }}>
        <div style={{ flex: 1 }}>
          {showLabels && (
            <label
              style={{
                fontSize: "13px",
                fontWeight: 600,
                color: "#333",
                marginBottom: "8px",
                display: "block",
              }}
            >
              Flat number <span style={{ color: "#C0392B" }}>*</span>
            </label>
          )}
          <input
            type="text"
            inputMode="numeric"
            value={flat || ""}
            disabled={disabled}
            // Digits only: the flat is what the floor is derived from, so
            // letting "G-605" or "605, 6th floor" in here would break it.
            onChange={(e) => onFlatChange(e.target.value.replace(/\D/g, "").slice(0, 4))}
            placeholder="e.g. 605"
            style={baseInputStyle}
          />
        </div>

        <div style={{ width: "96px" }}>
          {showLabels && (
            <label
              style={{
                fontSize: "13px",
                fontWeight: 600,
                color: "#888",
                marginBottom: "8px",
                display: "block",
              }}
            >
              Floor
            </label>
          )}
          <div
            style={{
              ...baseInputStyle,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#F0F0F0",
              color: derivedFloor === null ? "#AAAAAA" : "#333333",
              border: "0.4px solid #DDDDDD",
            }}
            title="Worked out from your flat number"
          >
            {derivedFloor === null ? "—" : floorLabel(derivedFloor)}
          </div>
        </div>
      </div>

      {flat && derivedFloor === null && (
        <p style={{ fontSize: "12px", color: "#C0392B", margin: "8px 0 0" }}>
          Flat number should be 3 or 4 digits, e.g. 605 or 1108.
        </p>
      )}
    </div>
  );
};

export default BlockPicker;
